import type { ExportFilters } from '../types/export.types';

/**
 * The row filters the full-page view offers — the subset of `ExportFilters`
 * that both `/templates/browse` and `/trigger` apply in SQL, so a product the
 * table shows under them is a product the file will contain.
 *
 * Deliberately NOT here: `fieldFilters` (the browse endpoint refuses it — it
 * matches rendered cells, so pages would come back ragged) and the browse-only
 * `search` / `columnFilters`, which the trigger does not accept and which the
 * export bar therefore has to call out as view-only.
 */
export interface BrowseExportFilters {
  successOnly: boolean;
  /** Post-enrichment overall score bounds, 0–10. Scheduled runs only.
   *  `undefined` is the unset state, so a bound can be cleared by assignment. */
  minScore?: number | undefined;
  maxScore?: number | undefined;
  /** Exact category values (`category` for GMC, `google_product_category` for META). */
  categories: string[];
  /** Explicit allow-list, capped by the server at 10,000. */
  productIds: string[];
  /** Translated locale to export (GMC only); `undefined` = English. */
  locale?: string | undefined;
}

/** The server's cap on `filters.productIds`; a longer list is a 422. */
export const MAX_PRODUCT_IDS = 10_000;

/** The shape both the URL and a fresh page start from. */
export const DEFAULT_BROWSE_EXPORT_FILTERS: BrowseExportFilters = {
  successOnly: true,
  categories: [],
  productIds: [],
};

/**
 * The wire `filters` for both the browse page and the export trigger. Keys are
 * omitted rather than sent empty so the request — and the query key built from
 * it — is identical whether a filter was cleared or never set.
 */
export function toExportFilters(filters: BrowseExportFilters): ExportFilters {
  return {
    successOnly: filters.successOnly,
    ...(filters.minScore !== undefined ? { minPostScore: filters.minScore } : {}),
    ...(filters.maxScore !== undefined ? { maxPostScore: filters.maxScore } : {}),
    ...(filters.categories.length > 0 ? { categories: filters.categories } : {}),
    ...(filters.productIds.length > 0 ? { productIds: filters.productIds } : {}),
    ...(filters.locale ? { locale: filters.locale } : {}),
  };
}

/**
 * How many filters narrow the export beyond the default. `successOnly` counts
 * only when switched OFF — on is the default and reads as no filter at all.
 * A score range is one filter however many of its two ends are set.
 */
export function countActiveExportFilters(filters: BrowseExportFilters): number {
  let count = 0;
  if (!filters.successOnly) count += 1;
  if (filters.minScore !== undefined || filters.maxScore !== undefined) count += 1;
  if (filters.categories.length > 0) count += 1;
  if (filters.productIds.length > 0) count += 1;
  if (filters.locale) count += 1;
  return count;
}

/**
 * Splits a pasted list of ids on commas, whitespace and newlines, dropping
 * blanks and duplicates while keeping first-seen order. The textarea accepts
 * whatever a spreadsheet column or a comma-joined cell pastes as.
 */
export function parseIdList(text: string): string[] {
  const seen = new Set<string>();
  for (const part of text.split(/[\s,]+/)) {
    const id = part.trim();
    if (id) seen.add(id);
  }
  return [...seen];
}

/** Clamps a typed score to the server's 0–10 range, or `undefined` for a blank. */
export function parseScore(text: string): number | undefined {
  if (text.trim() === '') return undefined;
  const value = Number(text);
  if (!Number.isFinite(value)) return undefined;
  return Math.min(10, Math.max(0, value));
}

/** A score as typed: digits with at most two decimals (`7`, `7.5`, `7.25`). */
const SCORE_TEXT = /^\d+(\.\d{0,2})?$/;

/** Whether a typed score is blank or a number within the server's 0–10 range, ≤ 2 decimals. */
export function isValidScoreText(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return true;
  const value = Number(trimmed);
  return SCORE_TEXT.test(trimmed) && Number.isFinite(value) && value >= 0 && value <= 10;
}

/** Keystroke guard for a score box: keeps a partial entry (`7.`), refuses a third decimal or a
 *  non-digit, so the box never holds what the filter cannot use. */
export function acceptScoreKeystroke(next: string, previous: string): string {
  return next === '' || /^\d{0,2}(\.\d{0,2})?$/.test(next) ? next : previous;
}
